// src/components/NoticeBoard.jsx
import { useEffect, useMemo, useState } from "react";
import { apiAxios } from "../cms/api/mockApi";

const defaultPageSize = 10;
const CATEGORIES = ["전체", "공지", "보도자료", "행사"];
const SORT_OPTIONS = [
  { value: "date-desc", label: "최신순" },
  { value: "date-asc",  label: "오래된순" },
  { value: "title-asc", label: "제목순(ㄱ-ㅎ)" },
];

// 사람이 읽기 좋은 파일 크기 표기 (e.g., 1.2 MB)
function formatBytes(bytes) {
  if (bytes == null) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = Number(bytes);
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  const fixed = n < 10 && i > 0 ? n.toFixed(1) : n.toFixed(0);
  return `${fixed} ${units[i]}`;
}

const formatDate = (iso) => {
  const d = new Date(iso);
  const mm = `${d.getMonth() + 1}`.padStart(2, "0");
  const dd = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}.${mm}.${dd}`;
};

const url = "/scms/api/bbs/selectBoardArticle.do";
const body = JSON.stringify({
  bbsId : "BBSMSTR_AAAAAAAAAAAA",
  useAt : "Y",
});

export default function NoticeBoard() {
  const [category, setCategory] = useState("전체");
  const [sort, setSort] = useState("date-desc");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [openId, setOpenId] = useState(null); // 현재 펼친 행/카드 id
  const [filesById, setFilesById] = useState({});
  
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const articles = async () => {
    try {
      setError(null);
      setItems(null);
      setLoading(true);

      apiAxios(url, body, function(res) {
        setItems(res.list);
      });

    } catch(e) {
      setError(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    articles();
  }, []);


  /** 필터 + 검색 */
  const filtered = useMemo(() => {
    if(items != null) {
      const byCategory =
      category === "전체" ? items : items.filter((i) => i.type === category);

      const kw = search.trim().toLowerCase();
      if (!kw) return byCategory;

      return byCategory.filter(
        (i) =>
          i.NTT_SJ.toLowerCase().includes(kw) ||
          i.NTT_CN.toLowerCase().includes(kw)
      );
    }
  }, [items, category, search]);
  
  /** 정렬 (조회순 제거됨) */
  const sorted = useMemo(() => {
    let arr = null;
    if(filtered != null) {
      arr = [...filtered];
      switch (sort) {
        case "date-asc":
          arr.sort((a, b) => +new Date(a.REG_DT) - +new Date(b.REG_DT));
          break;
        case "title-asc":
          arr.sort((a, b) => a.NTT_SJ.localeCompare(b.NTT_SJ, "ko"));
          break;
        case "date-desc":
        default:
          arr.sort((a, b) => +new Date(b.REG_DT) - +new Date(a.REG_DT));
      }
    }
    return arr;
  }, [filtered, sort]);

  if(loading) return <div> Loading... </div>
  if(error) return <div> 에러가 발생했습니다. </div>

  if(!items) return null; 

  /** 페이지네이션 */
  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const pageItems = sorted.slice(startIdx, startIdx + pageSize);

  const movePage = (p) => {
    if (p < 1 || p > totalPages) return;
    setOpenId(null);  // 이동 시 펼침 초기화
    setPage(p);
  };

  const onChangeCategory = (c) => {
    setCategory(c);
    setOpenId(null);
    setPage(1);
  };

  const onChangeSort = (e) => {
    setSort(e.target.value);
    setOpenId(null);
    setPage(1);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setOpenId(null);
    setPage(1);
  };

  const onChangePageSize = (e) => {
    setPageSize(Number(e.target.value));
    setOpenId(null);
    setPage(1);
  };

  const toggleRow = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
    const notice = sorted.find(x => x.ID === id);
    if (!notice) return;

    // 첨부아이디가 있고, 아직 안 불러온 경우만 조회
    if (notice.ATCH_FILE && !filesById[id]) {
      const url = "/scms/api/bbs/selectFileList.do";
      const body = JSON.stringify({ ATCH_FILE_ID: notice.ATCH_FILE });
      apiAxios(url, body, (res) => {
        setFilesById((m) => ({ ...m, [id]: res?.list || [] }));
      });
    }
  };


  return (
    <div className="notice-board">
      {/* 컨트롤 바 */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        {/* 카테고리 필터가 필요하면 주석 해제 */}
        {/* <ul className="nav nav-pills me-auto">
          {CATEGORIES.map((c) => (
            <li className="nav-item" key={c}>
              <button
                className={`nav-link ${category === c ? "active" : ""}`}
                onClick={() => onChangeCategory(c)}
              >
                {c}
              </button>
            </li>
          ))}
        </ul> */}

        <div className="d-flex align-items-center gap-2">
          <label className="text-secondary small">정렬</label>
          <select
            className="form-select form-select-sm"
            style={{ width: 160 }}
            value={sort}
            onChange={onChangeSort}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-2">
          <label className="text-secondary small">보기</label>
          <select
            className="form-select form-select-sm"
            style={{ width: 90 }}
            value={pageSize}
            onChange={onChangePageSize}
          >
            {[5, 10, 20].map((n) => (
              <option key={n} value={n}>
                {n}개
              </option>
            ))}
          </select>
        </div>

        <form onSubmit={onSearchSubmit} className="ms-2">
          <div className="input-group input-group-sm">
            <input
              className="form-control"
              placeholder="검색 (제목/내용)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn btn-primary">
              <img
                src={`/icon/search.png`}
                className="search-icon"
                width={"15px"}
                height={"auto"}
                alt=""
                aria-hidden="true"
              />
              검색
            </button>
          </div>
        </form>
      </div>

      {/* 데스크톱: 테이블 */}
      <div className="table-responsive soft-card p-0 d-none d-md-block">
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light sticky-top">
            <tr>
              <th style={{ width: 110 }}>분류</th>
              <th>제목</th>
              <th style={{ width: 150 }}>등록일</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((n) => {
              const opened = openId === n.ID;
              return (
                <tr key={n.ID}>
                  {/* ← 열린 행이면 위로 붙이기 */}
                  <td className={opened ? "align-top" : ""}>
                    <span className="badge rounded-pill bg-secondary-subtle text-secondary">
                      {n.TYPE}
                    </span>
                  </td>

                  <td>
                    <button
                      type="button"
                      className="btn btn-link p-0 fw-semibold link-dark text-decoration-none table-link-button"
                      onClick={() => toggleRow(n.ID)}
                      aria-expanded={opened}
                      aria-controls={`notice-panel-${n.ID}`}
                    >
                      {opened ? "▾" : "▸"} {n.NTT_SJ}
                    </button>

                    <div
                      id={`notice-panel-${n.ID}`}
                      className={`notice-collapse ${opened ? "show" : ""}`}
                    >
                      <div className="notice-detail soft-card p-3 mt-2">
                        <div className="d-flex justify-content-between align-items-start gap-3">
                          <div
                            className="mb-2"
                            style={{ whiteSpace: 'pre-line'}}
                          >
                            {n.NTT_CN}</div>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setOpenId(null)}
                          >
                            <i className="fa-solid fa-xmark fa-lg"></i>
                          </button>
                        </div>
                        {/* ✅ 첨부파일: 있을 때만 보여줌 */}
                        {Array.isArray(filesById[n.ID]) && filesById[n.ID].length > 0 && (
                          <div className="mt-3">
                            <div className="fw-semibold mb-2">
                              <i className="fa-solid fa-paperclip me-2"></i>첨부파일
                            </div>
                            <ul className="list-unstyled mb-0">
                              {filesById[n.ID].map((f, i) => (
                                <li key={f.ID || i} className="d-flex align-items-center gap-2">
                                  <i className="fa-regular fa-file"></i>
                                  {f.url ? (
                                    <a
                                      href={f.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      download={f.FILE_NM}
                                      className="link-dark text-decoration-none"
                                    >
                                      {f.FILE_NM ?? `첨부파일_${i + 1}`}
                                    </a>
                                  ) : (
                                    <span>{f.FILE_NM ?? `첨부파일_${i + 1}`}</span>
                                  )}
                                  {Number.isFinite(f.FILE_SIZE) && (
                                    <span className="text-muted small">({formatBytes(f.FILE_SIZE)})</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* ← 열린 행이면 위로 붙이기 */}
                  <td className={opened ? "align-top" : ""}>
                    {formatDate(n.REG_DT)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 모바일: 카드형 리스트 */}
      <div className="d-block d-md-none">
        <div className="list-group soft-card p-0">
          {pageItems.map((n) => {
            const opened = openId === n.ID;
            return (
              <div key={n.ID} className="list-group-item">
                <div className="d-flex justify-content-between align-items-start">
                  <span className="badge rounded-pill bg-secondary-subtle text-secondary">{n.TYPE}</span>
                  <small className="text-secondary">{formatDate(n.REG_DT)}</small>
                </div>
                <button
                  type="button"
                  className="btn btn-link p-0 mt-1 fw-semibold link-dark text-decoration-none"
                  onClick={() => toggleRow(n.ID)}
                  aria-expanded={opened}
                  aria-controls={`notice-m-panel-${n.ID}`}
                >
                  {opened ? "▾" : "▸"} {n.NTT_SJ}
                </button>
                <div
                  id={`notice-m-panel-${n.ID}`}
                  className={`notice-collapse ${opened ? "show" : ""}`}
                >
                  <div className="notice-detail soft-card p-3 mt-2">
                    <div className="mb-2">{n.NTT_CN}</div>
                    <div className="text-end">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setOpenId(null)}
                      >
                        닫기
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 하단: 개수 · 페이지네이션 */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mt-3">
        <div className="small text-secondary">
          총 <b>{total}</b>건 · {startIdx + 1}–{Math.min(startIdx + pageSize, total)}
        </div>
        <nav className="mt-2 mt-md-0">
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => movePage(safePage - 1)}>
                이전
              </button>
            </li>
            {Array.from({ length: totalPages }).map((_, i) => (
              <li
                key={i}
                className={`page-item ${safePage === i + 1 ? "active" : ""}`}
              >
                <button className="page-link" onClick={() => movePage(i + 1)}>
                  {i + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${safePage === totalPages ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => movePage(safePage + 1)}>
                다음
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
}
